// packages/auth/src/AuthProvider.tsx

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';

import {
  apiClient,
  clearAllMagicLinkSessions,
  queryClient,
  REFRESH_TOKEN_KEY,
  setAccessToken,
  getAccessToken,
  getAuthSessionRevision,
  invalidateAuthSession,
  isNetworkError,
  refreshSession,
} from '@alamo/api';
import type {
  ConsultantRegistrationStatus,
  ConsultantType,
  OnboardingCompletion,
  TokenPair,
  User,
  UserProfile,
} from '@alamo/types';
import { applyThemeMode } from '@alamo/types';

import { AUTH_PERMISSIONS_STALE_EVENT, AuthContext } from './AuthContext';
import type { AuthContextValue } from './AuthContext';
import { getStoredRefreshToken, setStoredRefreshToken, clearStoredRefreshToken } from './token';

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Backend wraps all responses in: { success: true, data: T, timestamp: string }
 */
interface ApiResponse<T> {
  success: boolean;
  data: T;
  timestamp: string;
}

interface LoginData {
  accessToken: string;
  refreshToken: string;
  // Backend returns this field as `requires2fa` (lowercase) — keep the wire
  // name exact so the check below actually matches. See api-alamo
  // auth.service.ts TwoFactorLoginResult.
  requires2fa?: boolean;
  tempToken?: string;
  user: {
    id: string;
    name: string;
    email: string;
    role_name: string | null;
  };
}

/**
 * Janela mínima entre revalidações de /auth/me disparadas por foco de aba.
 * Curta o suficiente para o status continuar fresco ao retomar o trabalho.
 */
const ME_REVALIDATE_MIN_INTERVAL_MS = 30_000;

export function AuthProvider({ children }: AuthProviderProps) {
  const lastRevalidateAtRef = useRef(0);
  const permissionRevalidationInFlightRef = useRef(false);
  const [user, setUser] = useState<User | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [roleName, setRoleName] = useState<string | null>(null);
  const [registrationStatus, setRegistrationStatus] = useState<ConsultantRegistrationStatus | null>(
    null,
  );
  const [consultantType, setConsultantType] = useState<ConsultantType | null>(null);
  const [commissionEligible, setCommissionEligible] = useState<boolean | null>(null);
  const [publicLinkSlug, setPublicLinkSlug] = useState<string | null>(null);
  const [onboardingCompletion, setOnboardingCompletion] = useState<OnboardingCompletion | null>(
    null,
  );

  const [isLoading, setIsLoading] = useState(true);

  const clearLocalSessionState = useCallback(() => {
    invalidateAuthSession();
    setAccessToken(null);
    clearAllMagicLinkSessions();
    setUser(null);
    setPermissions([]);
    setRoleName(null);
    setRegistrationStatus(null);
    setConsultantType(null);
    setCommissionEligible(null);
    setPublicLinkSlug(null);
    setOnboardingCompletion(null);
    queryClient.clear();
  }, []);

  // Espelho do isLoading pra ler dentro de handlers de evento sem re-registrar
  // listeners a cada mudança de estado (mantém as deps do effect estáveis).
  const isLoadingRef = useRef(isLoading);
  isLoadingRef.current = isLoading;

  const fetchMe = useCallback(async (revision = getAuthSessionRevision()) => {
    if (revision !== getAuthSessionRevision()) return;
    const meResponse = await apiClient.get<ApiResponse<UserProfile>>('/auth/me');
    if (revision !== getAuthSessionRevision()) return;
    const me = meResponse.data.data;

    const userObj: User = {
      id: me.id,
      email: me.email,
      name: me.name,
      phone: me.phone,
      schema_type: me.schema_type,
      is_active: me.is_active,
      last_login_at: null,
      createdAt: '',
      updatedAt: '',
      // W3.7-polish: copy tour completion so TourProvider can gate properly.
      // Without this the local user state stays { tourCompletedAt: undefined }
      // and the welcome tour re-fires on every mount of AppLayout.
      tourCompletedAt: me.tour_completed_at ?? null,
      // Identity reconfirmation gate — DataReconfirmationProvider opens the
      // blocking modal while requiresDataConfirmation is true; refreshUser()
      // after POST /profile/confirm-data flips it to false and closes the modal.
      identityConfirmedAt: me.identity_confirmed_at ?? null,
      requiresDataConfirmation: me.requires_data_confirmation ?? false,
    };

    setUser(userObj);
    setRoleName(me.role?.name ?? null);
    // /auth/me nests registration_status under `profile` (consultant_profiles);
    // fall back to the flat field for forward-compat. Reading only the flat
    // field left it undefined -> registrationStatus=null -> isApproved=true for
    // EVERY consultant (pre-approval / correction flows silently bypassed).
    setRegistrationStatus(me.profile?.registration_status ?? me.registration_status ?? null);
    setConsultantType(me.consultant_type ?? null);
    setCommissionEligible(me.commission_eligible ?? null);
    setPublicLinkSlug(me.public_link_slug ?? null);
    setOnboardingCompletion(me.onboarding_completion ?? null);

    // Apply the user's persisted theme locally (class + localStorage + event
    // that the ThemeProvider mirrors). APPLY-only: never re-persist here, or a
    // login→setMode→PATCH loop would form. Absent preference resolves to 'auto'.
    applyThemeMode(me.preferences?.theme?.mode ?? 'auto');

    // Backend is the single source of truth for permissions -- no fallback.
    setPermissions(me.permissions ?? []);
  }, []);

  // On mount, try to restore session.
  // 1. If access token already in memory (just logged in) -> only call /me
  // 2. If no access token but refresh token stored -> call /refresh then /me
  // 3. If neither -> not authenticated
  // Guard against React StrictMode double-invocation.
  const initStarted = useRef(false);
  useEffect(() => {
    if (initStarted.current) return;
    initStarted.current = true;

    const initAuth = async () => {
      const revision = getAuthSessionRevision();
      // Already has access token in memory (e.g. just logged in, SPA navigation)
      const existingToken = getAccessToken();
      if (existingToken) {
        try {
          await fetchMe(revision);
        } catch {
          if (revision !== getAuthSessionRevision()) return;
          // Token invalid/expired, try the shared single-flight refresh.
          const storedRefreshToken = getStoredRefreshToken();
          if (storedRefreshToken) {
            try {
              await refreshSession();
              await fetchMe(revision);
            } catch (refreshError) {
              if (revision !== getAuthSessionRevision()) return;
              // Network: refreshSession preservou o refresh token, próximo F5
              // tenta de novo. Auth real: refreshSession já limpou access +
              // refresh. Em ambos, zera o access token em memória — não há
              // sessão utilizável agora.
              setAccessToken(null);
              if (import.meta.env.DEV) {
                const isNet = isNetworkError(refreshError);
                console.warn(
                  isNet
                    ? '[AUTH] refresh failed by network — preservando refresh token'
                    : '[AUTH] refresh failed during init',
                  refreshError,
                );
              }
            }
          } else {
            setAccessToken(null);
          }
        } finally {
          setIsLoading(false);
        }
        return;
      }

      // No access token — try restore from refresh token
      const storedRefreshToken = getStoredRefreshToken();
      if (!storedRefreshToken) {
        setIsLoading(false);
        return;
      }

      try {
        await refreshSession();
        await fetchMe(revision);
      } catch (refreshError) {
        if (revision !== getAuthSessionRevision()) return;
        // Network: refreshSession preservou o refresh token, próximo F5 / boot
        // tenta de novo (usuário não é deslogado por reboot do backend).
        // Auth real: refreshSession já limpou access + refresh. Em ambos, zera
        // o access token em memória.
        setAccessToken(null);
        if (import.meta.env.DEV) {
          const isNet = isNetworkError(refreshError);
          console.warn(
            isNet
              ? '[AUTH] refresh failed on cold start by network — preservando refresh token'
              : '[AUTH] refresh failed on cold start',
            refreshError,
          );
        }
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, [fetchMe]);

  // localStorage é compartilhado entre abas. Quando uma delas remove o
  // refresh token durante logout, as demais abandonam imediatamente o access
  // token em memória e o estado autenticado, sem esperar os 15 min do JWT.
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === REFRESH_TOKEN_KEY && event.newValue === null) {
        clearLocalSessionState();
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [clearLocalSessionState]);

  // Revalida /auth/me quando a aba ganha foco/fica visível de novo.
  //
  // Sem isso, registrationStatus (e permissions/roleName/consultantType)
  // ficam presos no valor do momento do login pela sessão inteira — só
  // são buscados de novo em mount/refresh/login/2FA (nunca durante
  // navegação). Se um admin aprova/rejeita/solicita correção enquanto o
  // candidato já está logado numa aba aberta, o candidato continua vendo
  // isApproved calculado com o status antigo até deslogar ou dar F5
  // manualmente — foi exatamente o que aconteceu no relato do Pedro
  // (2026-07-03): o portal mostrava o menu completo de consultor aprovado
  // pra uma registration que já tinha correção solicitada.
  useEffect(() => {
    const revalidate = () => {
      // Não revalidar enquanto o boot ainda resolve a sessão — o initAuth já
      // busca /auth/me e um fetchMe concorrente aqui competiria com ele.
      if (isLoadingRef.current) return;
      if (!getAccessToken()) return;
      // `focus` e `visibilitychange` disparam juntos ao voltar para a aba, e
      // alternar de janela é gesto de segundos. Sem janela mínima, /auth/me
      // acumulou 12 mil chamadas por dia com poucos usuários — e cada request
      // atravessa os guards que consultam o banco. A janela preserva a intenção
      // (status fresco ao retomar a aba) e corta a rajada.
      const now = Date.now();
      if (now - lastRevalidateAtRef.current < ME_REVALIDATE_MIN_INTERVAL_MS) return;
      lastRevalidateAtRef.current = now;
      void fetchMe().catch(() => {
        // Best-effort — se o token expirou, os interceptors normais de
        // 401/refresh do apiClient assumem na próxima chamada real.
      });
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') revalidate();
    };

    window.addEventListener('focus', revalidate);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('focus', revalidate);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchMe]);

  useEffect(() => {
    const revalidatePermissions = () => {
      if (isLoadingRef.current || !getAccessToken() || permissionRevalidationInFlightRef.current) {
        return;
      }
      permissionRevalidationInFlightRef.current = true;
      void fetchMe()
        .catch(() => undefined)
        .finally(() => {
          permissionRevalidationInFlightRef.current = false;
        });
    };

    window.addEventListener(AUTH_PERMISSIONS_STALE_EVENT, revalidatePermissions);
    return () => window.removeEventListener(AUTH_PERMISSIONS_STALE_EVENT, revalidatePermissions);
  }, [fetchMe]);

  const login = useCallback(
    async (email: string, password: string) => {
      const revision = invalidateAuthSession();
      const response = await apiClient.post<ApiResponse<LoginData>>('/auth/login', {
        email,
        password,
      });
      if (revision !== getAuthSessionRevision()) return;
      const data = response.data.data;

      if (data.requires2fa) {
        // Store temp token for 2FA flow - caller should redirect to 2FA page
        throw { requires2fa: true, tempToken: data.tempToken };
      }

      // Drop any cache left from a prior identity before adopting the new one,
      // so the incoming user never reads server state scoped to someone else.
      clearAllMagicLinkSessions();
      queryClient.clear();
      setAccessToken(data.accessToken);
      setStoredRefreshToken(data.refreshToken);

      await fetchMe(revision);
    },
    [fetchMe],
  );

  const complete2FA = useCallback(
    async (tempToken: string, code: string) => {
      const revision = invalidateAuthSession();
      // NOTE: This calls /auth/2fa/complete, NOT /auth/2fa/verify.
      // /auth/2fa/verify is for enabling 2FA on an already-logged-in user (setup).
      // /auth/2fa/complete is for completing an in-progress login when 2FA is required.
      const response = await apiClient.post<ApiResponse<TokenPair>>('/auth/2fa/complete', {
        tempToken,
        code,
      });
      if (revision !== getAuthSessionRevision()) return;
      const data = response.data.data;
      // Drop any cache left from a prior identity before adopting the new one.
      clearAllMagicLinkSessions();
      queryClient.clear();
      setAccessToken(data.accessToken);
      setStoredRefreshToken(data.refreshToken);

      await fetchMe(revision);
    },
    [fetchMe],
  );

  const logout = useCallback(async () => {
    const revision = invalidateAuthSession();
    const storedRefreshToken = getStoredRefreshToken();
    try {
      await apiClient.post('/auth/logout', {
        refresh_token: storedRefreshToken,
      });
    } catch {
      // Logout may fail -- clear tokens regardless
    }
    if (revision !== getAuthSessionRevision()) return;
    clearStoredRefreshToken();
    clearLocalSessionState();
  }, [clearLocalSessionState]);

  const hydrateFromTokens = useCallback(
    async (tokens: { accessToken: string; refreshToken: string }) => {
      const revision = invalidateAuthSession();
      // Drop any cache left from a prior identity before adopting the new one.
      clearAllMagicLinkSessions();
      queryClient.clear();
      setAccessToken(tokens.accessToken);
      setStoredRefreshToken(tokens.refreshToken);
      await fetchMe(revision);
    },
    [fetchMe],
  );

  const refreshUser = useCallback(async () => {
    const revision = getAuthSessionRevision();
    // Shared single-flight refresh — grava access + refresh (rotação) e lança
    // se o refresh token estiver morto (limpando os tokens do storage).
    await refreshSession();
    await fetchMe(revision);
  }, [fetchMe]);

  // Users without a consultant_profile (admin, gerente) get registrationStatus=null
  // and are considered approved by default. Consultants count as approved from
  // `approved` onwards: a LOGGED-IN user necessarily has a password (first
  // access done), so `approved` here only exists while the backend promotion
  // approved→active lands — treating it as pending kept the "cadastro em
  // análise" banner forever for users who set the password before approval.
  const isApproved =
    registrationStatus === null ||
    registrationStatus === 'active' ||
    registrationStatus === 'approved';

  const value: AuthContextValue = useMemo(
    () => ({
      user,
      isAuthenticated: user !== null,
      isLoading,
      permissions,
      roleName,
      schemaType: null,
      registrationStatus,
      isApproved,
      consultantType,
      commissionEligible,
      publicLinkSlug,
      onboardingCompletion,
      login,
      complete2FA,
      logout,
      refreshUser,
      hydrateFromTokens,
    }),
    [
      user,
      isLoading,
      permissions,
      roleName,
      registrationStatus,
      isApproved,
      consultantType,
      commissionEligible,
      publicLinkSlug,
      onboardingCompletion,
      login,
      complete2FA,
      logout,
      refreshUser,
      hydrateFromTokens,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
